import React, { useState } from 'react';
import { Plus, Edit } from 'lucide-react';
import FormModal from './FormModal';
import type { ExpiryConfig } from '@domains/admin/api/accessControl.adapter';

interface ExpiryConfigModalProps {
  onClose: () => void;
  onSubmit: (config: ExpiryConfig) => Promise<void> | void;
  selectedExpiry: ExpiryConfig | null;
}

export const ExpiryConfigModal: React.FC<ExpiryConfigModalProps> = ({
  onClose,
  onSubmit,
  selectedExpiry,
}) => {
  const [config, setConfig] = useState<ExpiryConfig>(
    selectedExpiry || {
      id: 0,
      name: '',
      description: '',
      expiryType: 'user_access',
      expiryDays: 90,
      warningDays: 7,
      companyId: null,
      isActive: true,
      createdAt: '',
      updatedAt: '',
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit({ ...config, ...data });
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedExpiry?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Expiry Config' : 'Edit Expiry Config'}
      subtitle={isCreate ? 'Create a new expiry configuration' : 'Modify existing expiry config'}
      icon={isCreate ? <Plus className="text-white" size={24} /> : <Edit className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Config' : 'Update Config'}
      cancelText="Cancel"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
            Config Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="e.g., User Access Expiry"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={config.description}
            onChange={(e) => setConfig({ ...config, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Describe what this expiry config does"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="expiryType" className="block text-sm font-medium text-slate-700">
            Expiry Type *
          </label>
          <select
            id="expiryType"
            name="expiryType"
            value={config.expiryType}
            onChange={(e) => setConfig({ ...config, expiryType: e.target.value as any })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="user_access">User Access</option>
            <option value="company_license">Company License</option>
            <option value="document_retention">Document Retention</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="expiryDays" className="block text-sm font-medium text-slate-700">
              Expiry Days *
            </label>
            <input
              type="number"
              id="expiryDays"
              name="expiryDays"
              required
              min="1"
              value={config.expiryDays}
              onChange={(e) => setConfig({ ...config, expiryDays: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              placeholder="90"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="warningDays" className="block text-sm font-medium text-slate-700">
              Warning Days *
            </label>
            <input
              type="number"
              id="warningDays"
              name="warningDays"
              required
              min="0"
              value={config.warningDays}
              onChange={(e) => setConfig({ ...config, warningDays: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              placeholder="7"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={config.isActive}
            onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
            className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-500 focus:ring-offset-0"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-slate-700">
            Config is active
          </label>
        </div>
      </div>
    </FormModal>
  );
};